/**
 * AR (Accounts Receivable) reminder service.
 *
 * Sends buyer payment reminders / demands for overdue invoices, following the
 * framework Part G / Table 10 cadence. Email only (SMS is excluded in this MVP).
 *
 * Idempotent: each cadence step is gated on the per-bucket timestamp columns on
 * the invoice, so re-running never double-sends the same step.
 *
 * Framework Table 10 guardrail: the 30+ day FORMAL DEMAND requires Admin review
 * and signature — it is NOT auto-sent here. Those invoices are returned in
 * `requiresAdminSignoff` for an admin to action.
 */

import { supabase } from '../lib/supabase';
import { setCreditStatus, flaggedDealsFor } from './buyerService';
import { emailService } from './emailService';

export interface AROverdueRow {
  id: string;
  deal_id: string;
  /** Null on invoices raised before buyers became an entity. */
  buyer_id: string | null;
  invoice_number: string;
  buyer_name: string | null;
  buyer_email: string | null;
  buyer_mobile: string | null;
  /** The buyer-facing invoice token (migration 022). Drives the Pay Now link. */
  public_token: string | null;
  total_amount: number;
  due_date: string;
  status: string;
  days_since_due: number;
  bucket: ARBucket;
  already_sent: boolean;
  /**
   * The sale behind this invoice was amended after it was issued. Winnie, Checks_20 Sep §8:
   * the chase team must not ring a buyer about an amount or an identity that just changed.
   */
  flagged_amended: boolean;
  flagged_amended_reason: string | null;
}

export type ARBucket =
  | 'due_soon'      // day -1 .. 0
  | 'overdue_1_3'   // 1..3
  | 'call_4_6'      // 4..6 — a phone call, not an email (§5.4)
  | 'overdue_7_13'  // 7..13 (formal demand; credit suspended)
  | 'frozen_14_29'  // 14..29 (account frozen; all that buyer's deals flagged)
  | 'demand_30_plus'// 30+ (requires Admin sign-off — not auto-sent)
  | 'none';         // not yet due

export interface ARSendResult {
  processed: number;
  sent: number;
  skipped: number;
  requiresAdminSignoff: AROverdueRow[];
  errors: string[];
}

const DAY_MS = 1000 * 60 * 60 * 24;

function daysSinceDue(dueISO: string): number {
  const due = new Date(dueISO).getTime();
  const now = Date.now();
  return Math.floor((now - due) / DAY_MS);
}

/** Column that gates each bucket so we never re-send the same step. */
const GATE_COLUMN: Partial<Record<ARBucket, string>> = {
  due_soon: 'day_minus_1_reminder_sent_at',
  overdue_1_3: 'day_1_to_3_overdue_notice_sent_at',
  call_4_6: 'phone_task_created_at',
  overdue_7_13: 'day_7_formal_demand_sent_at',
  frozen_14_29: 'day_14_30_account_frozen_at',
  demand_30_plus: 'day_30_plus_collections_at',
};

function bucketFor(days: number): ARBucket {
  if (days >= -1 && days <= 0) return 'due_soon';
  if (days >= 1 && days <= 3) return 'overdue_1_3';
  // §5.4 days 4-7 is a phone call. There is no automated step here on purpose:
  // the ladder escalates from a reminder to a person picking up the phone, and
  // sending another email instead would be pretending that happened.
  if (days >= 4 && days <= 6) return 'call_4_6';
  if (days >= 7 && days <= 13) return 'overdue_7_13';
  if (days >= 14 && days <= 29) return 'frozen_14_29';
  if (days >= 30) return 'demand_30_plus';
  return 'none';
}

/**
 * What each rung does to the buyer's credit (§5.4).
 *
 * Before this, "credit suspended" and "account frozen" were a column name and a
 * timestamp. Nothing was suspended and nothing was frozen — a buyer 20 days
 * overdue could be sold another vehicle the same afternoon with no warning
 * anywhere on the screen.
 */
const CREDIT_FOR_BUCKET: Partial<Record<ARBucket, 'suspended' | 'frozen' | 'collections'>> = {
  overdue_7_13: 'suspended',
  frozen_14_29: 'frozen',
  demand_30_plus: 'collections',
};

export const AR_BUCKET_LABEL: Record<ARBucket, string> = {
  due_soon: 'Due within a day',
  overdue_1_3: 'Overdue 1–3 days',
  call_4_6: 'Call the buyer (4–6 days)',
  overdue_7_13: 'Formal demand · credit suspended',
  frozen_14_29: 'Account frozen',
  demand_30_plus: '30+ days · collections',
  none: 'Not yet due',
};

/**
 * Invoices that have been issued to the buyer and are not yet settled.
 *
 * 'partial' is included deliberately: recording a part payment must not drop an
 * invoice out of the reminder ladder, which is exactly what would happen if the
 * only open states were sent/overdue/disputed.
 */
async function fetchOpenInvoices() {
  const { data, error } = await supabase
    .from('invoices')
    .select(
      'id, deal_id, buyer_id, invoice_number, buyer_name, buyer_email, buyer_mobile, public_token, total_amount, due_date, status, ' +
        'flagged_amended, flagged_amended_reason, ' +
        'day_minus_1_reminder_sent_at, day_1_to_3_overdue_notice_sent_at, phone_task_created_at, ' +
        'day_7_formal_demand_sent_at, day_7_14_credit_suspended_at, day_14_30_account_frozen_at, day_30_plus_collections_at'
    )
    .in('status', ['sent', 'overdue', 'partial', 'disputed']);
  if (error) throw error;
  /*
   * The select list is built by string concatenation, so PostgREST's generic row
   * type is all TypeScript can infer from it. Naming the two fields this function
   * actually reasons about is enough, and keeps everything else callers read.
   */
  type OpenInvoiceRow = Record<string, any> & { id: string; deal_id?: string | null };
  const rows = (data || []) as unknown as OpenInvoiceRow[];

  /*
   * An invoice on an archived deal is not chased, and neither is one on a settled
   * sale. Winnie, 10 Sep: when the money received matches the sale, the invoice is
   * marked Settled and drops off this list. The settled_at check below is what does
   * the dropping - it reads the same field the green tick reads, rather than keeping
   * a second copy of the same fact.
   *
   * Archiving is how a vehicle is taken out of the book, and everything else in
   * the platform already respects it. This query did not, so a demand could go
   * out to a real buyer about a car that no longer appears anywhere - and an
   * email, once sent, cannot be unsent.
   */
  const dealIds = Array.from(
    new Set(rows.map((r) => r.deal_id).filter((id): id is string => Boolean(id))),
  );
  if (dealIds.length === 0) return rows;

  const { data: liveDeals, error: dealError } = await supabase
    .from('deals')
    .select('id')
    .in('id', dealIds)
    .is('deleted_at', null)
    .is('settled_at', null);
  if (dealError) throw dealError;

  const liveIds = new Set(((liveDeals || []) as unknown as Array<{ id: string }>).map((row) => row.id));
  return rows.filter((r) => !r.deal_id || liveIds.has(r.deal_id));
}

/**
 * Move the buyer's credit standing to match the rung the ladder has reached.
 *
 * Nothing here is inferred from a timestamp on an invoice: the status lives on
 * the buyer, which is why §5.4's "flag all their deals" is now expressible at
 * all. A buyer who is already further down the ladder is not pulled back up by
 * a second, less overdue invoice.
 */
const CREDIT_SEVERITY: Record<string, number> = { ok: 0, suspended: 1, frozen: 2, collections: 3 };

async function applyCreditConsequence(
  row: { buyer_id?: string | null; bucket: ARBucket; invoice_number: string; days_since_due: number },
  result: ARSendResult
): Promise<void> {
  const target = CREDIT_FOR_BUCKET[row.bucket];
  if (!target || !row.buyer_id) return;

  try {
    const { data: buyer } = await supabase
      .from('buyers')
      .select('credit_status')
      .eq('id', row.buyer_id)
      .maybeSingle();

    const current = (buyer as any)?.credit_status || 'ok';
    if (CREDIT_SEVERITY[current] >= CREDIT_SEVERITY[target]) return;

    await setCreditStatus(
      row.buyer_id,
      target,
      `${row.invoice_number} is ${row.days_since_due} days overdue.`
    );

    if (target === 'frozen') {
      const flagged = await flaggedDealsFor(row.buyer_id);
      result.errors.push(
        `Account frozen — ${flagged.length} open deal${flagged.length === 1 ? '' : 's'} for this buyer are now flagged.`
      );
    }
  } catch {
    // The reminder has already gone out; a failure to update the flag must not
    // make it look like the send failed.
  }
}

export const arService = {
  /** Read-only summary for the AR view. */
  async getOverdueSummary(): Promise<AROverdueRow[]> {
    const invoices = await fetchOpenInvoices();
    return invoices
      .map((inv: any): AROverdueRow => {
        const days = daysSinceDue(inv.due_date);
        const bucket = bucketFor(days);
        const gate = GATE_COLUMN[bucket];
        const already_sent = bucket !== 'none' && bucket !== 'demand_30_plus' ? !!inv[gate as string] : false;
        return {
          id: inv.id,
          deal_id: inv.deal_id,
          buyer_id: inv.buyer_id ?? null,
          invoice_number: inv.invoice_number,
          buyer_name: inv.buyer_name,
          buyer_email: inv.buyer_email,
          buyer_mobile: inv.buyer_mobile ?? null,
          public_token: inv.public_token ?? null,
          total_amount: Number(inv.total_amount) || 0,
          due_date: inv.due_date,
          status: inv.status,
          days_since_due: days,
          bucket,
          already_sent,
          flagged_amended: !!inv.flagged_amended,
          flagged_amended_reason: inv.flagged_amended_reason ?? null,
        };
      })
      .sort((a, b) => b.days_since_due - a.days_since_due);
  },

  /**
   * Send the due reminders/demands. Idempotent per bucket.
   * 30+ day formal demands are NOT sent — returned for admin sign-off.
   */
  async sendDueReminders(): Promise<ARSendResult> {
    const rows = await this.getOverdueSummary();
    const result: ARSendResult = {
      processed: 0,
      sent: 0,
      skipped: 0,
      requiresAdminSignoff: [],
      errors: [],
    };
    // The "Pay Now" button in every reminder email used to point at
    // window.location.origin — the app root, with no path — so a buyer who
    // clicked it got the DirectAuto login screen for a system they have no
    // account for. The buyer-facing invoice page already exists at
    // /invoice/<public_token> and is what the SMS path has always used; the
    // email now uses the same link. Built per invoice, because the token is
    // per invoice.
    const appOrigin =
      typeof window !== 'undefined' ? window.location.origin : 'https://staging.directauto.net.au';

    for (const row of rows) {
      result.processed += 1;

      if (row.bucket === 'none') {
        result.skipped += 1;
        continue;
      }
      if (row.bucket === 'demand_30_plus') {
        // Framework Table 10: formal demand needs Admin review + signature.
        result.requiresAdminSignoff.push(row);
        continue;
      }
      if (row.already_sent) {
        result.skipped += 1;
        continue;
      }
      if (!row.buyer_email) {
        result.errors.push(`${row.invoice_number}: no buyer email`);
        continue;
      }
      if (!row.public_token) {
        // Without a token there is no page to send them to, and a demand with
        // a dead Pay Now button is worse than a phone call. Migration 022
        // backfills every invoice, so this means something is genuinely wrong.
        result.errors.push(`${row.invoice_number}: no invoice link token — not sent`);
        continue;
      }
      const paymentLink = `${appOrigin}/invoice/${row.public_token}`;

      try {
        const dueDate = new Date(row.due_date).toLocaleDateString('en-AU');
        let sendRes;
        if (row.bucket === 'due_soon' || row.bucket === 'overdue_1_3') {
          sendRes = await emailService.sendPaymentReminder(
            row.buyer_email,
            row.buyer_name || 'Buyer',
            row.invoice_number,
            row.total_amount,
            dueDate,
            paymentLink
          );
        } else {
          // 7-13 and 14-29 → formal demand tone
          sendRes = await emailService.sendDemandLetter(
            row.buyer_email,
            row.buyer_name || 'Buyer',
            row.invoice_number,
            row.days_since_due,
            row.total_amount
          );
        }

        if (!sendRes.success) {
          result.errors.push(`${row.invoice_number}: ${sendRes.message}`);
          continue;
        }

        // Stamp the gate column + advance status.
        const gate = GATE_COLUMN[row.bucket] as string;
        const newStatus =
          row.bucket === 'due_soon' || row.bucket === 'overdue_1_3' ? 'overdue' : 'disputed';

        const stamp: Record<string, any> = {
          [gate]: new Date().toISOString(),
          status: newStatus,
        };
        // §5.4: the 7-13 rung is where credit is suspended. The column has
        // existed since migration 010 and was never written.
        if (row.bucket === 'overdue_7_13') {
          stamp.day_7_14_credit_suspended_at = new Date().toISOString();
        }

        await supabase.from('invoices').update(stamp).eq('id', row.id);

        // And the consequence on the buyer's account, which is the part that
        // actually stops another vehicle going out the door.
        await applyCreditConsequence(row, result);

        // Audit to workflow_logs.
        await supabase.from('workflow_logs').insert([
          {
            deal_id: row.deal_id,
            trigger_name: 'AR_REMINDER_SENT',
            trigger_status: 'success',
            success: true,
            error_message: null,
            details: { invoice_number: row.invoice_number, bucket: row.bucket, days_since_due: row.days_since_due },
            executed_at: new Date().toISOString(),
          },
        ]);

        result.sent += 1;
      } catch (e) {
        result.errors.push(`${row.invoice_number}: ${e instanceof Error ? e.message : 'send failed'}`);
      }
    }

    return result;
  },
};
